import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "select",
    "trigger",
    "triggerLabel",
    "triggerClearButton",
    "dialog",
    "search",
    "results",
    "emptyState",
    "clearButton",
  ]

  static values = {
    emptyMessage: String,
    selectedLabel: String,
  }

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleFocus = this.handleFocus.bind(this)
    this.bodyWasOverflowHidden = false
    this.ignoreNextWrappedSelectChange = false
    this.pageScrollX = 0
    this.syncFromSelect()
  }

  disconnect() {
    this.close()
  }

  open(event) {
    event?.preventDefault()
    if (!this.hasDialogTarget) {
      return
    }

    this.dialogTarget.classList.remove("hidden")
    this.dialogTarget.classList.add("flex")
    this.cacheBodyScrollState()
    document.addEventListener("keydown", this.boundHandleKeydown)
    document.addEventListener("focusin", this.boundHandleFocus)
    this.searchTarget.value = ""
    this.renderResults()

    window.requestAnimationFrame(() => {
      this.searchTarget.focus()
      this.scrollSelectedResultIntoView()
    })
  }

  close(event, { restoreFocus = Boolean(event) } = {}) {
    event?.preventDefault()
    if (!this.hasDialogTarget) {
      return
    }

    const wasOpen = !this.dialogTarget.classList.contains("hidden")
    if (!wasOpen) {
      document.removeEventListener("keydown", this.boundHandleKeydown)
      document.removeEventListener("focusin", this.boundHandleFocus)
      return
    }

    this.releaseDialogFocus()
    this.dialogTarget.classList.add("hidden")
    this.dialogTarget.classList.remove("flex")
    this.restoreBodyScrollState()
    document.removeEventListener("keydown", this.boundHandleKeydown)
    document.removeEventListener("focusin", this.boundHandleFocus)
    this.restoreViewportAfterClose()
    this.resultsTarget.replaceChildren()
    this.searchTarget.value = ""

    if (restoreFocus && this.hasTriggerTarget) {
      window.requestAnimationFrame(() => this.restoreTriggerFocus())
    }
  }

  filter() {
    this.renderResults()
  }

  pick(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value || ""
    this.applyValue(value)
    this.close(null, { restoreFocus: true })
  }

  clear(event) {
    event.preventDefault()
    this.applyValue("")
    this.close(event, { restoreFocus: true })
  }

  clearFromTrigger(event) {
    event.preventDefault()
    event.stopPropagation()
    this.applyValue("")
  }

  handleWrappedSelectChange() {
    this.syncFromSelect()

    if (this.ignoreNextWrappedSelectChange) {
      this.ignoreNextWrappedSelectChange = false
      return
    }

    this.dispatchChanged()
  }

  syncFromSelect() {
    if (!this.hasSelectTarget || !this.hasTriggerLabelTarget) {
      return
    }

    const selectedOption = this.selectTarget.selectedOptions?.[0]
    const hasValue = Boolean(this.selectTarget.value && selectedOption)
    this.triggerLabelTarget.textContent = hasValue ? selectedOption.textContent.trim() : this.placeholderText()

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("hidden", !hasValue)
      this.clearButtonTarget.classList.toggle("inline-flex", hasValue)
    }

    if (this.hasTriggerClearButtonTarget) {
      this.triggerClearButtonTarget.classList.toggle("hidden", !hasValue)
      this.triggerClearButtonTarget.classList.toggle("inline-flex", hasValue)
    }

    if (this.hasDialogTarget && !this.dialogTarget.classList.contains("hidden")) {
      this.renderResults()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close(event)
    }
  }

  handleFocus(event) {
    if (this.dialogTarget.classList.contains("hidden")) {
      return
    }

    if (!this.dialogTarget.contains(event.target)) {
      this.searchTarget.focus()
    }
  }

  renderResults() {
    if (!this.hasResultsTarget) {
      return
    }

    const fragment = document.createDocumentFragment()
    const query = this.normalizedQuery()
    const options = this.filteredOptions(query)

    this.resultsTarget.replaceChildren()

    options.forEach((option) => {
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.action = "public-identity-picker#pick"
      button.dataset.value = option.value
      button.dataset.publicIdentityPickerOption = "true"
      button.className = "flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-base text-slate-900 transition hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800"

      const label = document.createElement("span")
      label.className = "min-w-0 truncate"
      label.textContent = option.text
      button.append(label)

      if (option.value === this.selectTarget.value) {
        const indicator = document.createElement("span")
        indicator.className = "ml-3 shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-100"
        indicator.textContent = this.hasSelectedLabelValue ? this.selectedLabelValue : "Selected"
        button.append(indicator)
      }

      fragment.append(button)
    })

    this.resultsTarget.append(fragment)

    if (this.hasEmptyStateTarget) {
      const showEmpty = options.length === 0
      this.emptyStateTarget.classList.toggle("hidden", !showEmpty)
      this.emptyStateTarget.textContent = this.hasEmptyMessageValue ? this.emptyMessageValue : "No matches found."
    }
  }

  filteredOptions(query) {
    const options = this.options()
    if (!query) {
      return options
    }

    return options.filter((option) => this.normalize(option.text).includes(query))
  }

  options() {
    if (!this.hasSelectTarget) {
      return []
    }

    return Array.from(this.selectTarget.options)
      .filter((option) => option.value)
      .map((option) => ({
        value: option.value,
        text: option.textContent.trim(),
      }))
  }

  normalizedQuery() {
    return this.normalize(this.searchTarget.value)
  }

  normalize(value) {
    return (value || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
  }

  applyValue(value) {
    this.selectTarget.value = value

    if (window.jQuery && window.jQuery(this.selectTarget).data("select2")) {
      this.ignoreNextWrappedSelectChange = true
      window.jQuery(this.selectTarget).val(value).trigger("change")
      this.dispatchChanged()
      this.syncFromSelect()
    } else {
      this.selectTarget.dispatchEvent(new Event("change", {bubbles: true}))
    }
  }

  placeholderText() {
    return this.selectTarget.options[0]?.textContent?.trim() || ""
  }
  cacheBodyScrollState() {
    this.bodyWasOverflowHidden = document.body.classList.contains("overflow-hidden")
    this.pageScrollX = window.scrollX || document.documentElement.scrollLeft || document.body.scrollLeft || 0
    document.body.classList.add("overflow-hidden")
  }

  restoreBodyScrollState() {
    if (!this.bodyWasOverflowHidden) {
      document.body.classList.remove("overflow-hidden")
    }
  }

  releaseDialogFocus() {
    const activeElement = document.activeElement
    if (activeElement && this.dialogTarget.contains(activeElement)) {
      activeElement.blur()
    }
  }

  restoreViewportAfterClose() {
    const scrollX = this.pageScrollX || 0
    const reset = () => {
      document.documentElement.scrollLeft = scrollX
      document.body.scrollLeft = scrollX
      if (window.scrollX !== scrollX) {
        window.scrollTo(scrollX, window.scrollY)
      }

      const viewport = window.visualViewport
      if (viewport && ((viewport.scale && viewport.scale !== 1) || viewport.offsetLeft || viewport.offsetTop)) {
        window.scrollTo(window.scrollX, window.scrollY)
      }
    }

    reset()
    window.requestAnimationFrame(reset)
  }

  restoreTriggerFocus() {
    try {
      this.triggerTarget.focus({preventScroll: true})
    } catch (_error) {
      this.triggerTarget.focus()
      this.restoreViewportAfterClose()
    }
  }

  scrollSelectedResultIntoView() {
    const selectedOption = this.resultsTarget.querySelector(`[data-value="${CSS.escape(this.selectTarget.value)}"]`)
    selectedOption?.scrollIntoView({block: "nearest"})
  }

  dispatchChanged() {
    this.element.dispatchEvent(new CustomEvent("public-identity-picker:changed", {bubbles: true}))
  }
}
